import { jsx as _jsx } from "react/jsx-runtime";
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import isMobileDevice from 'is-mobile';
import { createRoot } from 'react-dom/client';
import { usePlaidLink } from 'react-plaid-link';
import { GoogleButton, onlyShowFocusOnTabs, prefetchLottie } from 'seagull';
import { IoContextProvider } from '~/contexts';
import { history } from '~/history';
import { PusherConnector } from '~/pusher';
import { AppWithEventReporter } from '../App';
import { sendErrReport } from '../infra/utils';
import { INTERCOM_HELP_CENTER_URL } from '../intercom/intercomLoadDiagnostics';
import { makeIntercomSupportClickEnvironment } from '../intercom/intercomSupportClickEnvironment';
import { WebIntercomClient } from '../intercom/webIntercomClient';
import { getHotjar } from '../io/adapters/hotjar';
import { loomParser } from '../io/adapters/loom';
import { makePersonaClient } from '../io/adapters/persona';
import { makePusher } from '../io/adapters/pusher';
import { makeWebAppleAuth } from '../io/adapters/webAppleAuth';
import { makeNativeChangeLocation, makeNativeLocationReload } from '../io/browser-navigation';
import { makeBrowserIo } from '../io/browserIo';
import { makeNativeOpenNewTab, makeOpenNewTab } from '../io/openNewTab';
import { applyFeatureFlagOverrideFromUrl } from '../providers/applyFeatureFlagOverride';
import { RealCookiesManager } from '../providers/CookiesManager';
import { RootDeviceProvider } from '../providers/DeviceProvider';
import { LazyReactPlayer, LazyYoutubePlayer, prefetchPlayers } from '../providers/lazyPlayers';
import { MobileViewportWidthProvider } from '../providers/MobileViewportWidthProvider';
import { initStoreApi } from '../store/store-api';
import { StoreProvider } from '../store/StoreProvider';
import { actualReactStripeJs } from '../stripe/reactStripeJs';
import { getTimezoneAbbreviation } from '../utils/date-timezone.utils';
import { initializeLogBIEvent, sendBiViaBeacon } from '../utils/logBIEvent/logBIEvent';
import { logBIEventOld } from '../utils/logBIEventOld';
import { prefetchPasswordPolicy } from '../utils/passwordStrength';
import { initSentry } from './initSentry';
import { MOUNT_NODE_ID } from './mountNode';
function prefetchHeavyMediaOnIdle() {
    const prefetch = ()=>{
        prefetchLottie();
        prefetchPlayers();
        prefetchPasswordPolicy();
    };
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(prefetch);
    } else {
        window.setTimeout(prefetch, 2000);
    }
}
export function mountApp(param) {
    let { nativeBridge, nativeAuthTokenStore, nativeIntercomClient } = param;
    var _window_googleIosClientId;
    initSentry(history);
    onlyShowFocusOnTabs();
    const gtmParams = {
        id: window._gtmId
    };
    // eslint-disable-next-line no-restricted-syntax -- root composition: defines the production getNow clock seam (frontend-code.md)
    const getNow = ()=>new Date();
    const windowHistoryIo = {
        getHistoryState: ()=>window.history.state,
        replaceHistoryState: (state)=>window.history.replaceState(state, '')
    };
    const cookies = new RealCookiesManager(window._authDomain, window.localStorage, nativeAuthTokenStore, nativeBridge.biometric);
    const logBIEvent = initializeLogBIEvent(sendBiViaBeacon);
    applyFeatureFlagOverrideFromUrl(cookies, window.location.search);
    const openNewTab = nativeBridge.isNative ? makeNativeOpenNewTab(nativeBridge.systemBrowser.openExternalUrl, (url)=>sendErrReport(new Error("native-open-url-refused: ".concat(url.split(':')[0])))) : makeOpenNewTab(function() {
        for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
            args[_key] = arguments[_key];
        }
        return window.open(...args);
    });
    const browser = makeBrowserIo({
        win: window,
        nav: navigator,
        perf: performance,
        url: URL,
        encodeUtf8: (input)=>new TextEncoder().encode(input),
        doc: document,
        pageShowWin: window,
        visibilityObserverCtor: window.IntersectionObserver
    }, {
        locationReload: nativeBridge.isNative ? makeNativeLocationReload({
            reload: ()=>window.location.reload(),
            showSplashScreen: nativeBridge.splashScreen.show
        }) : ()=>window.location.reload(),
        changeLocation: nativeBridge.isNative ? makeNativeChangeLocation({
            navigate: (url)=>window.location.href = url,
            showSplashScreen: nativeBridge.splashScreen.show,
            getLocation: ()=>({
                    href: window.location.href,
                    origin: window.location.origin
                })
        }) : (url)=>window.location.href = url,
        openNewTab,
        historyIo: windowHistoryIo,
        webAppleAuth: makeWebAppleAuth()
    });
    const root = createRoot(document.getElementById(MOUNT_NODE_ID));
    root.render(/*#__PURE__*/ _jsx(HistoryRouter, {
        // WHY: the app pins history@4 (kept for the native bridge); HistoryRouter types the v5+ History shape, bridged at runtime by the createURL/encodeLocation polyfill in ~/history.
        // eslint-disable-next-line no-restricted-syntax
        history: history,
        future: {
            v7_startTransition: true,
            v7_relativeSplatPath: false
        },
        children: /*#__PURE__*/ _jsx(IoContextProvider, {
            getNow: getNow,
            usePlaidLink: usePlaidLink,
            intercomClient: nativeIntercomClient !== null && nativeIntercomClient !== void 0 ? nativeIntercomClient : new WebIntercomClient({
                logBIEvent,
                openHelpCenter: ()=>void openNewTab(INTERCOM_HELP_CENTER_URL),
                supportClickEnv: makeIntercomSupportClickEnvironment({
                    logBIEvent,
                    openNewTab,
                    getNowMs: ()=>getNow().getTime()
                })
            }),
            getHotjar: getHotjar,
            personaClient: makePersonaClient,
            ReactPlayer: LazyReactPlayer,
            YoutubePlayer: LazyYoutubePlayer,
            GoogleButton: GoogleButton,
            loomParser: loomParser,
            cookies: cookies,
            logBIEventOld: logBIEventOld,
            logBIEvent: logBIEvent,
            localStorage: window.localStorage,
            sessionStorage: window.sessionStorage,
            reactStripe: actualReactStripeJs,
            getTimezoneAbbreviation: getTimezoneAbbreviation,
            browser: browser,
            native: nativeBridge,
            children: /*#__PURE__*/ _jsx(StoreProvider, {
                mgmtHost: window._mgmtHostname,
                apiGatewayHost: window._apiGatewayHostname,
                authDomain: window._authDomain,
                enableAppcues: window.enableAppcues,
                appcuesAccountId: window.appcuesAccountId,
                useAuthorizationHeader: window.useAuthorizationHeader,
                googleIosClientId: (_window_googleIosClientId = window.googleIosClientId) !== null && _window_googleIosClientId !== void 0 ? _window_googleIosClientId : '',
                sessionTimeoutMS: window.sessionTimeoutMS,
                initStoreApi: initStoreApi,
                children: /*#__PURE__*/ _jsx(RootDeviceProvider, {
                    isMobileDevice: isMobileDevice(),
                    isNativeApp: nativeBridge.isNative,
                    children: /*#__PURE__*/ _jsx(LocalizationProvider, {
                        dateAdapter: AdapterDateFns,
                        children: /*#__PURE__*/ _jsx(GTMProvider, {
                            state: gtmParams,
                            children: /*#__PURE__*/ _jsx(PusherConnector, {
                                makePusher: makePusher,
                                children: /*#__PURE__*/ _jsx(MobileViewportWidthProvider, {
                                    children: /*#__PURE__*/ _jsx(AppWithEventReporter, {})
                                })
                            })
                        })
                    })
                })
            })
        })
    }));
    nativeBridge.boot.markBundleAlive();
    prefetchHeavyMediaOnIdle();
}
